import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, Icon, Input, Segmented, toast } from "@sagot/ui";
import { useState } from "react";
import { useMembers } from "../../../roles/use-roles";
import { usePermissions } from "../../../session/permissions";
import { PageHeader } from "../shared/page-header";
import { useAudienceList } from "../shared/use-engage-data";
import { AnnouncementComposer } from "./announcement-composer";
import { AnnouncementList } from "./announcement-list";
import { AnnouncementStats } from "./announcement-stats";
import type { CardAction } from "./card-actions";
import { VIEWS, knownSegments, type Announcement, type AnnouncementView } from "./model";
import { useAnnouncementAction, useAnnouncementList, useDebounced } from "./use-announcements";

type ComposerState = { open: false } | { open: true; editing: Announcement | null };

const ACTION_TOASTS: Readonly<Record<CardAction, string>> = {
  send: "sentNow",
  duplicate: "duplicated",
  archive: "archived",
  unarchive: "restored",
  delete: "deleted",
};

function useLabels() {
  const audiences = useAudienceList();
  const members = useMembers();
  const audienceName = (code: number) => audiences.data?.find((a) => a.code === code)?.label ?? "";
  const audienceLabel = (codes: readonly number[]) => codes.map(audienceName).filter(Boolean).join(" · ");
  const authorName = (userId: string | null) => (userId ? (members.data?.find((m) => m.user_id === userId)?.name ?? null) : null);
  return { audienceName, audienceLabel, authorName };
}

/** `/w/:ws/announcements` (mockup tenant/announcements.html). */
export function AnnouncementsPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const canEdit = permissions.can("announcements.create") && !permissions.readOnly;
  const canSend = canEdit && permissions.can("announcements.send");
  const [view, setView] = useState<AnnouncementView>("all");
  const [search, setSearch] = useState("");
  const q = useDebounced(search.trim());
  const list = useAnnouncementList(view, q);
  const action = useAnnouncementAction();
  const [composer, setComposer] = useState<ComposerState>({ open: false });
  const labels = useLabels();
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];

  const onAction = (item: Announcement, kind: CardAction) => {
    action.mutate(
      { id: item.id, action: kind },
      {
        onSuccess: () => toast.success(t(`engage.announcements.toasts.${ACTION_TOASTS[kind]}`)),
        onError: () => toast.error(t("engage.announcements.toasts.failed")),
      },
    );
  };
  const openNew = () => {
    setComposer({ open: true, editing: null });
  };
  const newButton = canEdit ? (
    <Button onClick={openNew}>
      <Icon name="plus" />
      {t("engage.announcements.newAnnouncement")}
    </Button>
  ) : null;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <PageHeader title={t("engage.announcements.title")} subtitle={t("engage.announcements.subtitle")} actions={newButton} />
      <AnnouncementStats audienceName={labels.audienceName} />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Segmented
          aria-label={t("engage.announcements.views.label")}
          className="max-w-full overflow-x-auto"
          value={view}
          onValueChange={setView}
          options={VIEWS.map((v) => ({ value: v, label: t(`engage.announcements.views.${v}`) }))}
        />
        <div className="relative w-full sm:w-72">
          <Icon name="search" className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
          <Input
            type="search"
            className="h-9 pl-9"
            placeholder={t("engage.announcements.search")}
            aria-label={t("engage.announcements.search")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </div>
      </div>
      <AnnouncementList
        list={list}
        items={items}
        filtered={Boolean(q) || view !== "all"}
        emptyAction={newButton}
        cardProps={{
          canEdit,
          canSend,
          pendingId: action.isPending ? (action.variables.id) : null,
          onAction,
          onEdit: (item) => {
            setComposer({ open: true, editing: item });
          },
          ...labels,
        }}
      />
      <Dialog
        open={composer.open}
        onOpenChange={(open) => {
          if (!open) setComposer({ open: false });
        }}
      >
        {composer.open ? (
          <AnnouncementComposer
            key={composer.editing?.id ?? "new"}
            editing={composer.editing}
            canSend={canSend}
            segmentOptions={knownSegments(items)}
            onDone={() => {
              setComposer({ open: false });
            }}
          />
        ) : null}
      </Dialog>
    </div>
  );
}
